import AchievementCard from "../components/AchievementCard";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VerifyPhoneDialog from "../components/VerifyPhoneDialog";
import ProfileDialog from "../components/ProfileDialog";
import ReferralCard from "../components/ReferralCard";
import { CashbackPanel } from "../components/Cashback";
import WalletHeroCard from "../components/WalletHeroCard";
import IdentityVerificationDialog from "../components/IdentityVerificationDialog";
import VerifiedName from "../components/VerifiedName";
import { Phone, ChevronLeft, ChevronRight, Flame, Trophy, History, Megaphone, BadgeDollarSign, AlertTriangle, Gift, RotateCw } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";
import { BonusBadge } from "../lib/gameBonus";
import useLiveGames from "../lib/useLiveGames";
import {
  isPhoneBonusDeviceBlocked,
  PHONE_BONUS_DEVICE_BLOCKED_MESSAGE,
  showPhoneBonusResultToast,
} from "../lib/phoneBonus";

const FAKE_WINS = [
  "Jenny P wins $250 on Juwa 2.0",
  "Marcus L wins $480 on FireKirin",
  "Sofia R wins $120 on Vegas Sweeps",
  "Daniel K wins $1,200 on Cash Frenzy",
  "Aisha M wins $90 on Orion Stars",
  "Ben T wins $760 on GameVault",
];

const DAILY_SPIN_REVEAL_MS = 2600;

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rewardClaim, setRewardClaim] = useState(null);
  const [rewardClaiming, setRewardClaiming] = useState(false);
  const [dailySpin, setDailySpin] = useState(null);
  const [dailySpinClaiming, setDailySpinClaiming] = useState(false);
  const [dailySpinResult, setDailySpinResult] = useState(null);
  const [dailySpinResultVisible, setDailySpinResultVisible] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [achievementClaiming, setAchievementClaiming] = useState("");
  const hotGames = useLiveGames();
  const [gameBonuses, setGameBonuses] = useState({});
  const [communications, setCommunications] = useState({ notice_message: "", bad_beat_jackpot: 0, notifications: [] });
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [bonusClaiming, setBonusClaiming] = useState(false);
  const [birthdayClaiming, setBirthdayClaiming] = useState(false);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [hotAutoPaused, setHotAutoPaused] = useState(false);
  const carouselRef = useRef(null);
  const phoneBonusAutoClaimRef = useRef("");
  const dailySpinRevealTimer = useRef(null);

  const loadLeaderboardRewardClaim = () => (
    api.get("/me/leaderboard-reward")
      .then(({ data }) => setRewardClaim(data))
      .catch(() => setRewardClaim(null))
  );

  const loadAchievements = () => (
    api.get("/me/achievements")
      .then(({ data }) => setAchievements(data.achievements || []))
      .catch(() => setAchievements([]))
  );

  const loadDailySpin = () => (
    api.get("/me/daily-spin")
      .then(({ data }) => setDailySpin(data))
      .catch(() => setDailySpin(null))
  );

  useEffect(() => {
    api.get("/me/history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/leaderboard").then(({ data }) => setLeaderboard(data)).catch(() => {});
    loadLeaderboardRewardClaim();
    loadDailySpin();
    loadAchievements();
    api.get("/me/game-bonuses").then(({ data }) => setGameBonuses(data.bonuses || {})).catch(() => setGameBonuses({}));
    api.get("/communications").then(({ data }) => {
      setCommunications({
        ...(data.settings || {}),
        notifications: data.notifications || [],
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % FAKE_WINS.length), 3500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => () => {
    if (dailySpinRevealTimer.current) window.clearTimeout(dailySpinRevealTimer.current);
  }, []);

  useEffect(() => {
    if (hotAutoPaused || hotGames.length < 3) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const timer = setInterval(() => {
      const el = carouselRef.current;
      if (!el || document.hidden) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 8) return;

      const card = el.querySelector("[data-hot-game-card]");
      const cardWidth = card?.getBoundingClientRect().width || Math.min(260, el.clientWidth * 0.72);
      const step = cardWidth + 16;
      const next = el.scrollLeft + step;

      el.scrollTo({
        left: next >= maxScroll - 8 ? 0 : next,
        behavior: "smooth",
      });
    }, 2600);

    return () => clearInterval(timer);
  }, [hotAutoPaused, hotGames.length]);

  useEffect(() => {
    if (!user || !isPhoneBonusDeviceBlocked(user)) return;
    const marker = `${user.id || user.email || "player"}:${user.bonus_blocked_at || "blocked"}`;
    const storageKey = `sophia-phone-bonus-blocked:${marker}`;
    if (window.localStorage.getItem(storageKey)) return;
    window.localStorage.setItem(storageKey, "1");
    toast.warning(PHONE_BONUS_DEVICE_BLOCKED_MESSAGE, { duration: 7000 });
  }, [user]);

  useEffect(() => {
    if (!user?.phone_verified || user?.bonus_claimed || isPhoneBonusDeviceBlocked(user) || !hasRequiredFreeplayProfile(user)) return undefined;
    const marker = `${user.id || user.email || "player"}:${user.phone_verified_at || user.phone || "verified"}`;
    if (phoneBonusAutoClaimRef.current === marker) return undefined;
    phoneBonusAutoClaimRef.current = marker;
    let cancelled = false;

    setBonusClaiming(true);
    api.post("/auth/phone/bonus/claim")
      .then(async ({ data }) => {
        if (cancelled) return;
        if (Number(data?.free_play_credited || 0) > 0 || isPhoneBonusDeviceBlocked(data)) {
          showPhoneBonusResultToast(toast, data);
        }
        await refreshUser?.();
      })
      .catch(() => {
        phoneBonusAutoClaimRef.current = "";
      })
      .finally(() => {
        if (!cancelled) setBonusClaiming(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshUser, user]);

  if (!user) return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Sign in to view your dashboard</h1>
        <Link to="/login" className="px-8 py-3 bg-[#D4AF37] text-black font-bold rounded-full inline-block">Sign in</Link>
      </div>
    </div>
  );

  const scroll = (dir) => carouselRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  const hasFreeplayProfileDetails = hasRequiredFreeplayProfile(user);
  const phoneBonusNeedsProfile = user.phone_verified && !user.bonus_claimed && !hasFreeplayProfileDetails;
  const canClaimPhoneBonus = user.phone_verified && hasFreeplayProfileDetails && !user.bonus_claimed;
  const phoneBonusDeviceBlocked = isPhoneBonusDeviceBlocked(user);
  const requireFreeplayProfile = () => {
    if (hasFreeplayProfileDetails) return true;
    toast.warning("Add your full legal name, date of birth, and state before claiming Freeplay.");
    setProfileOpen(true);
    return false;
  };
  const claimLeaderboardReward = async () => {
    if (rewardClaiming) return;
    if (!requireFreeplayProfile()) return;
    setRewardClaiming(true);
    try {
      const { data } = await api.post("/me/leaderboard-reward/claim");
      setRewardClaim(data);
      await refreshUser?.();
      await api.get("/leaderboard").then(({ data: rows }) => setLeaderboard(rows)).catch(() => {});
      toast.success(`${moneyFromDollars(data?.reward?.reward_amount || data?.transaction?.amount / 1000 || 0)} Freeplay claimed`);
    } catch (err) {
      toast.error(formatApiError(err));
      await loadLeaderboardRewardClaim();
    } finally {
      setRewardClaiming(false);
    }
  };
  const claimPhoneBonus = async () => {
    if (bonusClaiming) return;
    if (!requireFreeplayProfile()) return;
    setBonusClaiming(true);
    try {
      const { data } = await api.post("/auth/phone/bonus/claim");
      await refreshUser?.();
      showPhoneBonusResultToast(toast, data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBonusClaiming(false);
    }
  };
  const claimBirthdayBonus = async () => {
    if (birthdayClaiming) return;
    if (!requireFreeplayProfile()) return;
    setBirthdayClaiming(true);
    try {
      await api.post("/me/birthday-bonus/claim");
      await refreshUser?.();
      toast.success("$5 Birthday Freeplay claimed");
    } catch (err) {
      toast.error(formatApiError(err));
      await refreshUser?.();
    } finally {
      setBirthdayClaiming(false);
    }
  };
  const claimDailySpin = async () => {
    if (dailySpinClaiming) return null;
    if (!requireFreeplayProfile()) return null;
    if (dailySpinRevealTimer.current) window.clearTimeout(dailySpinRevealTimer.current);
    setDailySpinClaiming(true);
    setDailySpinResult(null);
    setDailySpinResultVisible(false);
    try {
      const { data } = await api.post("/me/daily-spin/claim");
      setDailySpin(data);
      const reward = data?.reward || {
        id: data?.transaction?.daily_spin_reward_id,
        label: data?.transaction?.daily_spin_reward_label,
        amount: data?.transaction?.amount,
      };
      setDailySpinResult(reward || null);
      dailySpinRevealTimer.current = window.setTimeout(async () => {
        setDailySpinResultVisible(true);
        setDailySpinClaiming(false);
        await refreshUser?.();
        const amount = Number(data?.transaction?.amount || reward?.amount || 0);
        toast.success(amount > 0 ? `${moneyFromCoins(amount)} Freeplay won` : (reward?.label || "Daily spin completed"));
      }, DAILY_SPIN_REVEAL_MS);
      return reward || null;
    } catch (err) {
      toast.error(formatApiError(err));
      await loadDailySpin();
      setDailySpinClaiming(false);
      return null;
    }
  };
  const claimAchievement = async (achievement) => {
    if (!achievement?.id || achievementClaiming) return;
    if (!requireFreeplayProfile()) return;
    setAchievementClaiming(achievement.id);
    try {
      const { data } = await api.post(`/me/achievements/${achievement.id}/claim`);
      setAchievements(data.achievements || []);
      await refreshUser?.();
      if (data.already_claimed) toast.info("This reward was already credited. Your progress is up to date.");
      else toast.success(`${data?.achievement?.reward_label || "$0.00 Freeplay"} claimed`);
    } catch (err) {
      toast.error(formatApiError(err));
      await loadAchievements();
    } finally {
      setAchievementClaiming("");
    }
  };

  return (
    <div className="min-h-screen overflow-x-clip bg-[#030705] text-white">
      <Navbar />

      <section className="max-w-7xl mx-auto w-full px-3 sm:px-6 md:px-10 pt-5 sm:pt-8 md:pt-10 pb-14 sm:pb-16">
        <div className="rounded-full bg-black/40 border border-white/10 px-2 py-1.5 flex items-center gap-3 text-sm overflow-hidden mb-8" data-testid="live-wins-ticker">
          <span className="px-3 py-0.5 rounded-full bg-[#D4AF37] text-black font-bold text-xs uppercase tracking-wider whitespace-nowrap shrink-0">Live wins</span>
          <span className="text-white/80 truncate flex-1 fade-up" key={tickerIdx}>{FAKE_WINS[tickerIdx]}</span>
        </div>

        {(communications.notice_message || Number(communications.bad_beat_jackpot || 0) > 0 || communications.notifications?.length > 0) && (
          <div className="mb-6 rounded-2xl border border-[#D4AF37]/30 bg-[#0A130E] p-4 sm:p-5">
            <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
              <div className="min-w-0">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                  <Megaphone className="h-4 w-4" />
                  Club notice
                </div>
                {communications.notice_message ? (
                  <p className="whitespace-pre-line text-sm text-white/80">{communications.notice_message}</p>
                ) : (
                  <p className="text-sm text-white/50">Watch this space for {siteConfig.name} updates.</p>
                )}
                {communications.notifications?.[0] && (
                  <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3">
                    <div className="text-sm font-semibold">{communications.notifications[0].title}</div>
                    <div className="mt-1 text-sm text-white/60">{communications.notifications[0].message}</div>
                  </div>
                )}
              </div>
              {Number(communications.bad_beat_jackpot || 0) > 0 && (
                <div className="rounded-xl border border-[#D4AF37]/30 bg-[#141007] px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/50">
                    <BadgeDollarSign className="h-4 w-4 text-[#D4AF37]" />
                    Bad-beat jackpot
                  </div>
                  <div className="mt-1 text-2xl font-black text-[#D4AF37]">${Number(communications.bad_beat_jackpot || 0).toFixed(2)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <WalletHeroCard
          user={user}
          className="mb-6"
          onBalanceChange={() => refreshUser?.()}
          onKycRequired={() => setIdentityOpen(true)}
        />

        {phoneBonusDeviceBlocked && (
          <div
            data-testid="phone-bonus-device-blocked"
            className="mb-10 rounded-2xl border border-amber-500/40 bg-amber-950/30 p-5 flex items-start gap-4"
          >
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-amber-400/40 bg-amber-400/15">
              <AlertTriangle className="h-5 w-5 text-amber-300" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg leading-tight text-amber-100">Phone bonus already used on this device</h3>
              <p className="mt-1 text-sm leading-relaxed text-amber-100/75">
                {PHONE_BONUS_DEVICE_BLOCKED_MESSAGE} You can still add money and play normally.
              </p>
            </div>
          </div>
        )}

        {(!user.phone_verified || canClaimPhoneBonus || phoneBonusNeedsProfile) && (
          <div data-testid="verify-phone-banner"
            className="mb-10 rounded-2xl border-2 border-purple-500/40 bg-gradient-to-r from-purple-900/40 via-purple-900/20 to-[#0a0a1f] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-purple-500 grid place-items-center shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-lg leading-tight">
                  {canClaimPhoneBonus ? "Claim your $5 Freeplay" : phoneBonusNeedsProfile ? "Complete profile to claim $5 Freeplay" : "Verify your number and claim $5 Freeplay"}
                </h3>
                <p className="text-sm text-white/60 mt-0.5">
                  {canClaimPhoneBonus
                    ? "Your phone is verified. Tap claim to add it to your Freeplay balance."
                    : phoneBonusNeedsProfile
                      ? "Full legal name, date of birth, and state are required before Freeplay is added."
                      : "Freeplay loads into games only. It is not wallet cash."}
                </p>
              </div>
            </div>
            <button
              onClick={canClaimPhoneBonus ? claimPhoneBonus : phoneBonusNeedsProfile ? () => setProfileOpen(true) : () => setVerifyOpen(true)}
              disabled={bonusClaiming}
              data-testid="open-verify-phone"
              className="w-full sm:w-auto px-5 py-2.5 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] whitespace-nowrap shrink-0 disabled:opacity-60"
            >
              {canClaimPhoneBonus ? bonusClaiming ? "Claiming..." : "Claim $5 FP" : phoneBonusNeedsProfile ? "Complete profile" : "Verify"}
            </button>
          </div>
        )}

        {user.birthday_bonus_available && (
          <div
            data-testid="birthday-bonus-banner"
            className="mb-10 rounded-2xl border-2 border-[#D4AF37]/45 bg-gradient-to-r from-[#171006] via-[#10140A] to-[#07110B] p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-center gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#D4AF37] text-black">
                <Gift className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold leading-tight">Claim your $5 Birthday Freeplay</h3>
                <p className="mt-0.5 text-sm text-white/60">Available today only. Freeplay loads into games only.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={claimBirthdayBonus}
              disabled={birthdayClaiming}
              className="w-full shrink-0 rounded-full bg-[#D4AF37] px-5 py-2.5 font-bold text-black hover:bg-[#F1C40F] disabled:opacity-60 sm:w-auto"
            >
              {birthdayClaiming ? "Claiming..." : "Claim $5 FP"}
            </button>
          </div>
        )}

        <CashbackPanel refreshKey={user?.coins} onClaim={() => refreshUser?.()} />

        <DailyRewardsCard
          dailySpin={dailySpin}
          dailySpinClaiming={dailySpinClaiming}
          dailySpinResult={dailySpinResultVisible ? dailySpinResult : null}
          onDailySpin={claimDailySpin}
        />

        {achievements.length > 0 && (
          <section className="mb-12 rounded-2xl border border-white/10 bg-white p-5 text-slate-950 shadow-[0_20px_70px_rgba(0,0,0,0.25)] sm:p-7">
            <div className="mb-6">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#2f78b7]">
                <Gift className="h-4 w-4" />
                Rewards
              </div>
              <h2 className="text-2xl font-black tracking-tight text-black">Achievements</h2>
              <p className="mt-2 text-sm text-slate-700">Complete goals and claim Freeplay rewards.</p>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              {achievements.map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  busy={achievementClaiming === achievement.id}
                  onClaim={() => claimAchievement(achievement)}
                />
              ))}
            </div>
          </section>
        )}

        <div className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <div className="flex min-w-0 items-center gap-2.5">
              <Flame className="w-5 h-5 text-orange-400" />
              <h2 className="font-display text-xl font-bold sm:text-2xl md:text-3xl">Hot Games</h2>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/games" className="text-sm text-[#D4AF37] hover:underline">View all</Link>
              <button onClick={() => scroll(-1)} aria-label="Scroll left"
                className="p-2 rounded-full border border-white/10 hover:bg-white/5 hidden md:inline-flex">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => scroll(1)} aria-label="Scroll right"
                className="p-2 rounded-full border border-white/10 hover:bg-white/5 hidden md:inline-flex">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={carouselRef}
            onPointerEnter={() => setHotAutoPaused(true)}
            onPointerLeave={() => setHotAutoPaused(false)}
            onTouchStart={() => setHotAutoPaused(true)}
            onTouchEnd={() => setHotAutoPaused(false)}
            className="hot-games-carousel -mx-4 sm:mx-0 flex max-w-[100vw] gap-4 overflow-x-auto overscroll-x-contain px-4 sm:px-0 pb-3 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="hot-games-carousel">
            {hotGames.map((g, index) => (
              <Link key={g.id} to={`/games/${g.id}`} data-testid={`hot-game-${g.id}`}
                data-hot-game-card
                style={{ "--hot-index": index }}
                className="hot-game-card snap-start w-[38vw] max-w-36 shrink-0 sm:w-44 sm:max-w-none md:w-52 group">
                <div className="hot-game-art relative aspect-[5/4] rounded-2xl overflow-hidden border-2 border-transparent group-hover:border-[#D4AF37]/60 transition-all">
                  <BonusBadge game={g} bonuses={gameBonuses} className="absolute right-2 top-2 z-10" />
                  {g.image_url ? (
                    <img src={g.image_url} alt={g.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0B2115] to-[#030705]" />
                  )}
                </div>
                <div className="mt-3 text-center font-semibold">{g.name}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid min-w-0 gap-6 md:grid-cols-3">
          <div className="min-w-0 rounded-2xl border border-white/5 bg-[#0A130E] p-5 sm:p-6">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4"><History className="w-4 h-4" /> Recent activity</div>
            <div className="space-y-1.5 text-sm">
              {history.length === 0 && <div className="text-white/40 py-6 text-center">No activity yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-1.5 border-b border-white/5">
                  <span className="font-mono text-white/60">{h.reels?.join(" - ") || "-"}</span>
                  <span className={h.payout > 0 ? "text-[#D4AF37] font-semibold" : "text-white/40"}>
                    {h.payout > 0 ? `+${h.payout.toLocaleString("en-US")}` : `-${h.bet.toLocaleString("en-US")}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="min-w-0 rounded-2xl border border-white/5 bg-[#0A130E] p-5 sm:p-6">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4"><Trophy className="w-4 h-4" /> Top players</div>
            <div className="space-y-1.5 text-sm">
              {leaderboard.slice(0, 5).map((u, i) => (
                <div key={u.username + i} className="flex min-w-0 items-center justify-between gap-3 py-1.5 border-b border-white/5">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-display font-bold gold-text w-6 shrink-0">#{i + 1}</span>
                    <VerifiedName user={u} name={u.username} className="min-w-0" />
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block tabular-nums text-white/70">{leaderMoney(u)}</span>
                    {u.reward && (
                      <span className="mt-0.5 inline-flex rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5 text-[10px] font-black text-[#F8E08E]">
                        {u.reward.label}
                      </span>
                    )}
                    {rewardClaim?.eligible && Number(rewardClaim.rank) === i + 1 && (
                      <button
                        type="button"
                        onClick={claimLeaderboardReward}
                        disabled={rewardClaiming || rewardClaim.claimed || !rewardClaim.claim_open}
                        className="mt-1 block w-full rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-black text-emerald-200 disabled:border-white/10 disabled:bg-white/5 disabled:text-white/40"
                      >
                        {leaderboardClaimLabel(rewardClaim, rewardClaiming)}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {rewardClaim?.eligible && (
              <p className="mt-3 text-xs leading-relaxed text-white/40">
                Top-player freeplay claims open Saturday. Reward freeplay redeems $20-$50 with 40% credited to wallet and 60% deducted.
              </p>
            )}
          </div>

          <ReferralCard />
        </div>
      </section>

      <VerifyPhoneDialog open={verifyOpen} onClose={() => setVerifyOpen(false)} />
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <IdentityVerificationDialog open={identityOpen} onClose={() => setIdentityOpen(false)} onSubmitted={() => refreshUser?.()} user={user} />
      <Footer />
    </div>
  );
}

function hasRequiredFreeplayProfile(user) {
  const fullName = String(user?.full_name || "").trim().replace(/\s+/g, " ");
  return Boolean(fullName.split(" ").length >= 2 && user?.date_of_birth && user?.state);
}

function leaderMoney(row) {
  const coins = Number(row?.total_load_coins ?? row?.coins ?? 0);
  return `$${(coins / 1000).toFixed(2)}`;
}

function DailyRewardsCard({ dailySpin, dailySpinClaiming, dailySpinResult, onDailySpin }) {
  const [spinRotation, setSpinRotation] = useState(0);
  if (!dailySpin) return null;
  const spinClaimable = Boolean(dailySpin?.claimable);
  const spinReward = dailySpinResult || (!dailySpinClaiming ? dailySpin?.reward : null);
  const segments = dailySpin?.segments?.length ? dailySpin.segments : [
    { id: "ten-cents", label: "$0.10", amount: 0.1 },
    { id: "twenty-cents", label: "$0.20", amount: 0.2 },
    { id: "thirty-cents", label: "$0.30", amount: 0.3 },
    { id: "bonus-20", label: "20% bonus", amount: 0 },
    { id: "better-luck", label: "Better luck", amount: 0 },
    { id: "bonus-30", label: "30% bonus", amount: 0 },
    { id: "one-dollar", label: "$1.00", amount: 1 },
    { id: "bonus-50", label: "50% bonus", amount: 0 },
  ];
  const wheelSegments = segments.some((item) => String(item?.label || "").toLowerCase() === "30% bonus")
    ? segments.slice(0, 8)
    : [...segments.slice(0, -1), { id: "bonus-30", label: "30% bonus", amount: 0 }, ...segments.slice(-1)].slice(0, 8);
  const wheelBackground = "radial-gradient(circle at 50% 50%, transparent 0 35%, rgba(90,74,48,0.14) 35.5% 36.5%, transparent 37%), conic-gradient(from -22.5deg,#FBF8F0 0 12.5%,#ECE6DA 12.5% 25%,#FFFDF8 25% 37.5%,#E5DED1 37.5% 50%,#FBF8F0 50% 62.5%,#EDE7DC 62.5% 75%,#FFFDF8 75% 87.5%,#E7E0D4 87.5% 100%)";
  const handleDailySpinClick = async () => {
    if (!spinClaimable || dailySpinClaiming) return;
    const reward = await onDailySpin?.();
    if (!reward) return;
    const rewardIndex = dailySpinRewardIndex(wheelSegments, reward);
    setSpinRotation((current) => nextDailySpinRotation(current, rewardIndex));
  };

  return (
    <section className="mb-12 rounded-2xl border border-sky-400/20 bg-[#081421] p-5 text-white shadow-[0_20px_70px_rgba(0,0,0,0.25)] sm:p-7">
      <div className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#2F78B7]">
        <Gift className="h-4 w-4" />
        Daily Rewards
      </div>
      <div className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-[#050B14] p-4 sm:p-5">
        <div className="mb-3 text-center">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Daily Spin</div>
          <h3 className="mt-1 text-2xl font-black">Ready to spin?</h3>
        </div>
        <div className="relative mx-auto h-[330px] w-full max-w-[390px] sm:h-[390px]">
          <div className="absolute left-1/2 top-0 z-20 h-12 w-7 -translate-x-1/2 rounded-b-full bg-gradient-to-b from-[#FFE08A] to-[#F3A300] shadow-lg shadow-amber-500/30 ring-1 ring-amber-700/10" />
          <div className="absolute left-1/2 top-6 h-[292px] w-[292px] -translate-x-1/2 overflow-hidden rounded-full border-[12px] border-[#D8D0C1] shadow-2xl shadow-black/35 sm:h-[340px] sm:w-[340px] sm:border-[14px]">
            <div
              className="absolute inset-0 rounded-full"
              style={{ background: wheelBackground, transform: `rotate(${spinRotation}deg)`, transition: "transform 2600ms cubic-bezier(0.12, 0.72, 0.12, 1)" }}
            >
              <div className="absolute inset-0 rounded-full shadow-[inset_0_0_34px_rgba(96,80,55,0.20)]" />
              <div className="absolute inset-[35%] rounded-full border-2 border-[#9AD9C5]/80 bg-[#FFFDF8]/95 shadow-inner shadow-slate-300/70" />
              {wheelSegments.map((item, index) => {
                const angle = (index * 45) - 90;
                const radius = 38;
                const x = 50 + Math.cos((angle * Math.PI) / 180) * radius;
                const y = 50 + Math.sin((angle * Math.PI) / 180) * radius;
                return (
                  <span
                    key={`${item.id || item.label}-${index}`}
                    className="absolute flex max-w-[78px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-0.5 text-center text-[11px] font-black leading-none text-[#2B2822] drop-shadow-[0_1px_1px_rgba(255,255,255,0.95)] sm:max-w-[84px] sm:text-[12px]"
                    style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${angle + 90}deg)` }}
                  >
                    <span className="text-lg leading-none sm:text-xl" aria-hidden="true">{dailySpinPrizeIcon(item.label)}</span>
                    <span>{item.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        {spinReward && (
          <div className="mb-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-center text-sm font-bold text-emerald-200">
            Result: {dailySpinPrizeIcon(spinReward.label)} {spinReward.label || (Number(spinReward.amount || 0) > 0 ? `${moneyFromCoins(spinReward.amount)} Freeplay` : "Daily spin")}
          </div>
        )}
        {dailySpin?.locked_reason && !spinClaimable && (
          <div className="mb-3 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-300">
            {dailySpin.locked_reason}
          </div>
        )}
        <button
          type="button"
          onClick={handleDailySpinClick}
          disabled={!spinClaimable || dailySpinClaiming}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 font-black text-slate-950 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RotateCw className={`h-4 w-4 ${dailySpinClaiming ? "animate-spin" : ""}`} />
          {dailySpinClaiming ? "Spinning..." : dailySpin?.claimed ? "Spin tomorrow" : "Spin"}
        </button>
      </div>
    </section>
  );
}

function dailySpinPrizeIcon(label) {
  const text = String(label || "").toLowerCase();
  if (text.includes("bonus")) return "\u{1F381}";
  if (text.includes("luck")) return "\u{1F340}";
  return "\u{1F4B0}";
}

function dailySpinRewardIndex(segments, reward) {
  if (!reward || !Array.isArray(segments) || segments.length === 0) return 0;
  const rewardId = String(reward.id || reward.daily_spin_reward_id || "").trim();
  if (rewardId) {
    const idIndex = segments.findIndex((item) => String(item?.id || "").trim() === rewardId);
    if (idIndex >= 0) return idIndex;
  }
  const rewardLabel = normalizeDailySpinLabel(reward.label || reward.daily_spin_reward_label);
  if (rewardLabel) {
    const labelIndex = segments.findIndex((item) => normalizeDailySpinLabel(item?.label) === rewardLabel);
    if (labelIndex >= 0) return labelIndex;
  }
  return 0;
}

function normalizeDailySpinLabel(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function nextDailySpinRotation(current, rewardIndex) {
  const segmentAngle = 45;
  const target = (((-Number(rewardIndex || 0) * segmentAngle) % 360) + 360) % 360;
  const currentMod = ((Number(current || 0) % 360) + 360) % 360;
  const delta = (target - currentMod + 360) % 360;
  return Number(current || 0) + 1440 + delta;
}

function moneyFromDollars(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function moneyFromCoins(value) {
  return `$${(Number(value || 0) / 1000).toFixed(2)}`;
}

function leaderboardClaimLabel(claim, busy) {
  if (busy) return "Claiming...";
  if (claim?.claimed) return "Claimed";
  if (!claim?.claim_open) return "Claim Saturday";
  return "Claim";
}
